import { useState } from 'react';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Plus, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FloatingLabelInput } from '@/shared/components/custom/form-field/FloatingLabelInput';
import { FieldTypeList, FIELD_TYPE } from '@/features/custom-fields/public';
import type { FieldType } from '@/features/custom-fields/public';
import { customFieldPopoverSchema } from '../schemas';
import type { CustomFieldPopoverFormValues } from '../schemas';

interface CustomFieldPopoverFormProps {
  onAdd: (values: CustomFieldPopoverFormValues) => void;
  isPending?: boolean;
}

const DEFAULT_VALUES: CustomFieldPopoverFormValues = { name: '', type: FIELD_TYPE.Text };
const OPTIONS_FIELD_TYPES: FieldType[] = [FIELD_TYPE.Select, FIELD_TYPE.MultiSelect];

export function CustomFieldPopoverForm({ onAdd, isPending = false }: CustomFieldPopoverFormProps) {
  const { t } = useTranslation('projectTemplates');
  const [open, setOpen] = useState(false);
  const [optionsText, setOptionsText] = useState('');

  const { control, handleSubmit, reset, setValue } = useForm<CustomFieldPopoverFormValues>({
    resolver: zodResolver(customFieldPopoverSchema),
    defaultValues: DEFAULT_VALUES,
  });
  const { field: typeField } = useController({ control, name: 'type' });
  const needsOptions = OPTIONS_FIELD_TYPES.includes(typeField.value as FieldType);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      reset(DEFAULT_VALUES);
      setOptionsText('');
    }
  }

  function handleOptionsTextChange(text: string) {
    setOptionsText(text);
    const options = text
      .split(',')
      .map((option) => option.trim())
      .filter(Boolean);
    // Keeps `options` inside RHF state so zodResolver sees it at submit time —
    // the input itself is plain (not schema-shaped as a comma-separated string).
    setValue('options', options);
  }

  function handleFormSubmit(values: CustomFieldPopoverFormValues) {
    onAdd({ ...values, options: needsOptions ? values.options : undefined });
    handleOpenChange(false);
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="ml-1 size-7 shrink-0"
              aria-label={t('preview.addField')}
              data-testid="project-templates-field-add-button"
            >
              <Plus className="size-3.5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{t('preview.addField')}</TooltipContent>
      </Tooltip>

      <PopoverContent
        className="w-72 p-3"
        align="start"
        data-testid="project-templates-field-popover"
      >
        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-3">
          <p className="text-sm font-medium">{t('popovers.field.title')}</p>

          <FormFieldInput
            control={control}
            name="name"
            label={t('popovers.field.nameLabel')}
            floatingLabel
            disabled={isPending}
            data-testid="project-templates-field-name-input"
          />

          <FieldTypeList
            selectedType={typeField.value as FieldType}
            onSelectType={(type) => typeField.onChange(type)}
            compact
          />

          {needsOptions && (
            <FloatingLabelInput
              id="project-templates-field-options"
              label={t('popovers.field.optionsLabel')}
              disabled={isPending}
              value={optionsText}
              hasValue={optionsText.length > 0}
              onChange={(e) => handleOptionsTextChange(e.target.value)}
              data-testid="project-templates-field-options-input"
            />
          )}

          <Button
            type="submit"
            size="sm"
            disabled={isPending}
            data-testid="project-templates-field-submit-button"
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('popovers.field.add')}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}
